/**
 * The `@` menu: what it can point at, when it opens, and what pressing a row puts in the draft.
 *
 * ## Two groups, and only one of them can be wrong
 *
 * `MentionMenu` splits at a 6px break: above it is material Deck already holds, below it are the
 * connected apps it has to reach through. Only the apps carry a status word, and the component's own
 * note says why — "a Figma connection can stop working, an artifact you already own cannot".
 *
 * So the workspace list is the deck's real contents, handed down from the container that owns them,
 * and the apps list is only ever as long as the statuses we can actually read.
 * `useIntegrationStatuses` knows about Figma; nothing in this product connects Notion, so Notion is
 * not offered — a row reading "Notion · Ready" would be the one lie the whole status column exists
 * to prevent. When there is nothing truthful to put below the break, the group is empty and
 * `MentionMenu` drops the break with it.
 *
 * ## The chip that is not a chip
 *
 * `MentionChip` needs `ComposerTextRow`'s flow content model, and the live composer paints its text
 * with a `<textarea>` laid over that row (see `LiveComposer`'s header — one painter, no divergence).
 * A committed mention is therefore plain text in the draft, not a chip. The agent reads the prompt
 * as a string either way, so nothing downstream can tell; what is lost is the picture, and getting
 * it back means a contenteditable, which is a different composer.
 */
import type { MentionMenuItem } from '~/design-system/components/v1_5/Chat';
import type { DirectoryItem } from '~/design-system/components/v1_5/Directory';

/** Everything the deck holds that the agent can be pointed at. Order is the directory's own. */
export function workspaceMentions(
  artifacts: DirectoryItem[],
  documents: DirectoryItem[],
): MentionMenuItem[] {
  return [
    ...artifacts.map(
      (item): MentionMenuItem => ({
        source: 'artifact',
        name: item.title,
        // Verbatim from the row — `artifact 01 · 2h ago` is the string the directory already prints
        // beside this item, and two spellings of one item's subtitle is how they drift.
        description: item.meta,
      }),
    ),
    ...documents.map(
      (item): MentionMenuItem => ({
        source: 'document',
        name: item.title,
        description: item.meta,
      }),
    ),
  ];
}

/**
 * The connected apps, with the only two statuses this pane can tell the truth about.
 *
 * Web is not a connection — it is the agent's own search, always reachable — so it reports `Ready`
 * unconditionally. Figma reports what `useIntegrationStatuses` says.
 */
export function appMentions(figmaConnected: boolean): MentionMenuItem[] {
  return [
    {
      source: 'figma',
      name: 'Figma',
      description: 'connects Deck to Figma',
      status: figmaConnected ? 'Ready' : 'Needs auth',
    },
    { source: 'web', name: 'Web', description: 'search the web', status: 'Ready' },
  ];
}

/** The `@…` run the caret is currently inside, if any. */
export interface MentionQuery {
  /** Index of the `@` itself. */
  start: number;
  /** Index just past the caret — the run is `[start, end)`. */
  end: number;
  /** What was typed after the `@`, lowercased for matching. */
  needle: string;
}

/**
 * Is the caret inside an `@` run?
 *
 * The `@` must open a word — start of the draft, or after whitespace — so an email address does not
 * open a menu, and the run ends at the first space, so the menu closes once the user has moved on.
 * Everything is measured against the caret rather than the end of the string, because editing in the
 * middle of a sentence is the normal case.
 */
export function mentionQueryAt(text: string, caret: number): MentionQuery | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;

  const prior = at === 0 ? '' : before[at - 1];
  if (prior && !/\s/.test(prior)) return null;

  const needle = before.slice(at + 1);
  // A space closes the run. Names with spaces are still insertable — they just are not re-parsed
  // back into a query once committed, which is the same trade every plain-text mention makes.
  if (/\s/.test(needle)) return null;

  return { start: at, end: caret, needle: needle.toLowerCase() };
}

/**
 * How many rows each group may put on screen.
 *
 * Not a style choice — a correctness one. `MentionMenu` is a `MenuSurface`, which hugs its rows and
 * has no scroller of its own, and the composer anchors it at `bottom: calc(100% + 8px)` so it grows
 * UPWARD. A real deck in this workspace has 29 documents; unlimited, the surface measured 1196px
 * tall with its top at −606, i.e. two thirds of it off the top of the screen and the first rows
 * unreachable. Capping is also what an autocomplete is: the top matches, narrowed by typing, not the
 * whole library. Six and three keep the surface around 320px, which is the size the artboard draws.
 */
export const MENTION_WORKSPACE_LIMIT = 6;
export const MENTION_APPS_LIMIT = 3;

/** Only the rows that match what has been typed after the `@`, and only as many as will fit. */
export function filterMentions(
  items: MentionMenuItem[],
  needle: string,
  limit: number,
): MentionMenuItem[] {
  const matched = needle
    ? items.filter((item) => item.name.toLowerCase().includes(needle))
    : items;
  return matched.slice(0, limit);
}

/** The draft with the `@` run replaced by the chosen name, and where the caret lands after. */
export function applyMention(
  text: string,
  query: MentionQuery,
  name: string,
): { text: string; caret: number } {
  const inserted = `@${name} `;
  return {
    text: `${text.slice(0, query.start)}${inserted}${text.slice(query.end)}`,
    caret: query.start + inserted.length,
  };
}

/** One run of the draft: prose, or a name that came out of the `@` menu. */
export interface MentionSegment {
  /** The characters themselves. A mention run includes its leading `@` and never its trailing space. */
  text: string;
  /** The menu name this run is. Absent on prose. */
  name?: string;
  /** Where the run starts in the source string — unique, so it is also the render key. */
  start: number;
}

/**
 * The draft, cut into prose and mentions.
 *
 * A committed mention is plain text, so the only thing that can say where one starts and stops is
 * the list of names it could have come from. That is the right tool for a SENT message, which is a
 * string off the wire and nothing else — and the wrong one for the live draft, which knows what it
 * inserted and uses {@link segmentsFromRuns} instead. See {@link remapRuns} for why that matters.
 *
 * Two rules keep it from finding mentions that are not there. The `@` must OPEN a word, so an
 * address is prose; and the name must END one, so `@Web` does not claim the first four characters
 * of `@Website` and leave `site` stranded beside it. Longest name first, so one name being another's
 * prefix never takes the shorter bite.
 *
 * Whitespace is deliberately left in the prose runs rather than trimmed: the composer draws this
 * over a `<textarea>` and every space has to be in exactly the place the field put it.
 */
export function splitMentions(text: string, names: readonly string[]): MentionSegment[] {
  const ordered = [...names].sort((a, b) => b.length - a.length);
  const segments: MentionSegment[] = [];
  let plain = 0;
  let at = 0;
  while (at < text.length) {
    const opensWord = at === 0 || /\s/.test(text[at - 1]);
    if (text[at] !== '@' || !opensWord) {
      at += 1;
      continue;
    }
    const name = ordered.find((candidate) => {
      if (!text.startsWith(`@${candidate}`, at)) return false;
      const after = text[at + candidate.length + 1];
      return after === undefined || !/[\p{L}\p{N}]/u.test(after);
    });
    if (name === undefined) {
      at += 1;
      continue;
    }
    if (at > plain) segments.push({ text: text.slice(plain, at), start: plain });
    segments.push({ text: `@${name}`, name, start: at });
    at += name.length + 1;
    plain = at;
  }
  if (plain < text.length) segments.push({ text: text.slice(plain), start: plain });
  return segments;
}

/** A committed mention in the draft, as a range. `[start, end)` covers `@Name`, never its space. */
export interface MentionRun {
  start: number;
  end: number;
}

/**
 * The draft's mention ranges, carried across an edit.
 *
 * ## Why the draft tracks ranges at all, when {@link splitMentions} can find them
 *
 * Because name matching is not STABLE under typing, and the instability is visible. Type
 * `@Payment API Latency` and it is prose; type the word `Reduction` after it and the whole run
 * becomes a mention the instant the last letter lands — text the user wrote turning into an object
 * they never picked, going full white under the caret (Beka, 2026-09-20). Every name in the menu is
 * a sentence of ordinary words, so this is not an edge case, it is what happens when someone types
 * out a title instead of picking it.
 *
 * So the live draft remembers what the MENU put there, and a keystroke can only move a range or
 * destroy it — never mint one. `splitMentions` still owns the other direction, where there is
 * nothing to remember: a sent message is a string off the wire, and re-reading it by name is the
 * only thing anyone can do with it.
 *
 * ## The edit
 *
 * Any change to a text box, however it arrived, is ONE splice: the common prefix and the common
 * suffix are what survived, everything between them was replaced. That is enough to place every
 * range — before the splice it does not move, after it shifts by the length delta, and across it
 * the range is gone, because editing inside a name is how you stop meaning it.
 *
 * Typing immediately after a mention is the case that has to be right, and it is: an insertion at
 * exactly `end` is "before the splice" by this rule, so the mention stays and the new letters are
 * prose.
 */
export function remapRuns(
  runs: readonly MentionRun[],
  before: string,
  after: string,
): MentionRun[] {
  if (before === after) return [...runs];
  const shortest = Math.min(before.length, after.length);
  let head = 0;
  while (head < shortest && before[head] === after[head]) head += 1;
  let tail = 0;
  while (
    tail < shortest - head &&
    before[before.length - 1 - tail] === after[after.length - 1 - tail]
  ) {
    tail += 1;
  }
  const cutStart = head;
  const cutEnd = before.length - tail;
  const delta = after.length - tail - head - (cutEnd - cutStart);
  const kept: MentionRun[] = [];
  for (const run of runs) {
    if (run.end <= cutStart) kept.push(run);
    else if (run.start >= cutEnd) kept.push({ start: run.start + delta, end: run.end + delta });
  }
  return kept;
}

/**
 * The draft cut into prose and mentions from RANGES rather than by matching — the twin of
 * {@link splitMentions} for the one surface that knows what it inserted. See {@link remapRuns}.
 *
 * A range that no longer fits the string is dropped rather than trusted: nothing should be able to
 * slice the draft at an index the draft does not have, however the two got out of step.
 */
export function segmentsFromRuns(text: string, runs: readonly MentionRun[]): MentionSegment[] {
  const ordered = [...runs].sort((a, b) => a.start - b.start);
  const segments: MentionSegment[] = [];
  let plain = 0;
  for (const run of ordered) {
    if (run.start < plain || run.end > text.length || run.end <= run.start) continue;
    if (run.start > plain) segments.push({ text: text.slice(plain, run.start), start: plain });
    segments.push({
      text: text.slice(run.start, run.end),
      // Everything past the `@`. The container resolves by name, and the name is what was inserted.
      name: text.slice(run.start + 1, run.end),
      start: run.start,
    });
    plain = run.end;
  }
  if (plain < text.length) segments.push({ text: text.slice(plain), start: plain });
  return segments;
}
